import pb from './pocketbase.js';

/**
 * Service pour gérer les messages de contact
 */
class ContactService {
  
  /**
   * Envoie un message de contact
   * @param {Object} messageData - Les données du message
   * @param {string} messageData.name - Nom de l'expéditeur
   * @param {string} messageData.email - Email de l'expéditeur
   * @param {string} messageData.message - Contenu du message
   * @returns {Promise<Object>} Le message créé
   */
  async sendMessage(messageData) {
    try {
      // Ajouter des métadonnées
      const enrichedData = {
        ...messageData,
        status: 'nouveau',
        ip_address: await this.getClientIP(),
        user_agent: navigator.userAgent
      };

      // Créer le message dans PocketBase
      const record = await pb.collection('contact_messages').create(enrichedData);
      
      // Envoyer une notification push
      await this.sendPushNotification(record);
      
      return record;
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      throw error;
    }
  }

  /**
   * Récupère l'IP du client (approximative)
   */
  async getClientIP() {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      return 'Inconnue';
    }
  }

  /**
   * Envoie une notification push sur l'appareil
   * @param {Object} messageRecord - L'enregistrement du message
   */
  async sendPushNotification(messageRecord) {
    try {
      // Vérifier si les notifications sont supportées
      if (!('Notification' in window)) {
        console.log('Les notifications ne sont pas supportées par ce navigateur');
        return;
      }

      // Demander la permission si nécessaire
      let permission = Notification.permission;
      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }

      if (permission === 'granted') {
        // Créer la notification
        const notification = new Notification('🔔 Nouveau message de contact', {
          body: `Vous avez reçu un nouveau message de ${messageRecord.name}. Consultez votre interface admin pour le lire et y répondre.`,
          icon: '/faviconx.ico',
          badge: '/favicon.ico',
          tag: 'contact-message',
          requireInteraction: true
        });

        // Gérer les clics sur la notification
        notification.onclick = () => {
          window.focus();
          // Rediriger vers l'admin si possible
          if (window.location.pathname !== '/admin') {
            window.open('/admin', '_blank');
          }
          notification.close();
        };

        // Pas d'auto-fermeture - reste jusqu'à interaction manuelle

        console.log('🔔 Notification push envoyée');
      } else {
        console.log('Permission de notification refusée');
      }
      
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la notification:', error);
    }
  }


  /**
   * Vérifie si SMTP est activé
   */
  async isSmtpEnabled() {
    try {
      const setting = await pb.collection('app_settings').getFirstListItem('key="smtp_enabled"');
      return setting.value === 'true';
    } catch (error) {
      return false;
    }
  }

  /**
   * Récupère l'email de notification depuis les paramètres
   */
  async getNotificationEmail() {
    try {
      const setting = await pb.collection('app_settings').getFirstListItem('key="contact_notification_email"');
      return setting.value;
    } catch (error) {
      // Si le paramètre n'existe pas, utiliser l'email par défaut
      return 'contact@couleursirene.fr';
    }
  }

  /**
   * Formate le contenu de l'email de notification
   */
  formatEmailContent(messageRecord) {
    return `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <div style="background: linear-gradient(135deg, #82c0c0, #4a90a4); padding: 20px; text-align: center;">
          <h1 style="color: white; margin: 0;">Nouveau Message de Contact</h1>
          <p style="color: #f0f8ff; margin: 5px 0;">Couleur Sirène</p>
        </div>
        
        <div style="padding: 20px; background: #f9f9f9;">
          <h2 style="color: #2c3e50; border-bottom: 2px solid #82c0c0; padding-bottom: 10px;">
            Détails du message
          </h2>
          
          <div style="background: white; padding: 15px; border-radius: 8px; margin: 10px 0;">
            <p><strong>Nom :</strong> ${messageRecord.name}</p>
            <p><strong>Email :</strong> <a href="mailto:${messageRecord.email}">${messageRecord.email}</a></p>
            <p><strong>Date :</strong> ${new Date(messageRecord.created).toLocaleString('fr-FR')}</p>
            <p><strong>IP :</strong> ${messageRecord.ip_address}</p>
          </div>
          
          <div style="background: white; padding: 15px; border-radius: 8px; margin: 10px 0;">
            <h3 style="color: #2c3e50; margin-top: 0;">Message :</h3>
            <div style="background: #f8f9fa; padding: 15px; border-left: 4px solid #82c0c0; border-radius: 4px;">
              ${messageRecord.message.replace(/\n/g, '<br>')}
            </div>
          </div>
          
          <div style="text-align: center; margin: 20px 0;">
            <a href="https://couleursirene.infocolab.online/_/" 
               style="background: #82c0c0; color: white; padding: 12px 24px; text-decoration: none; border-radius: 25px; display: inline-block;">
              Voir dans l'admin PocketBase
            </a>
          </div>
        </div>
        
        <div style="background: #2c3e50; color: white; padding: 15px; text-align: center; font-size: 12px;">
          <p>Ce message a été envoyé automatiquement depuis le site Couleur Sirène</p>
          <p>Ne pas répondre à cet email - Répondre directement à : ${messageRecord.email}</p>
        </div>
      </div>
    `;
  }

  /**
   * Récupère tous les messages de contact (pour l'admin)
   */
  async getMessages(page = 1, perPage = 50) {
    try {
      return await pb.collection('contact_messages').getList(page, perPage, {
        sort: '-created'
      });
    } catch (error) {
      console.error('Erreur lors de la récupération des messages:', error);
      throw error;
    }
  }

  /**
   * Marque un message comme lu
   */
  async markAsRead(messageId) {
    try {
      return await pb.collection('contact_messages').update(messageId, {
        status: 'lu'
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du message:', error);
      throw error;
    }
  }

  /**
   * Marque un message comme traité
   */
  async markAsProcessed(messageId) {
    try {
      return await pb.collection('contact_messages').update(messageId, {
        status: 'traité'
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du message:', error);
      throw error;
    }
  }

  /**
   * Configure l'email de notification
   */
  async setNotificationEmail(email) {
    try {
      // Essayer de mettre à jour le paramètre existant
      try {
        const existing = await pb.collection('app_settings').getFirstListItem('key="contact_notification_email"');
        return await pb.collection('app_settings').update(existing.id, {
          value: email,
          description: 'Email de notification pour les messages de contact'
        });
      } catch (notFound) {
        // Si le paramètre n'existe pas, le créer
        return await pb.collection('app_settings').create({
          key: 'contact_notification_email',
          value: email,
          description: 'Email de notification pour les messages de contact'
        });
      }
    } catch (error) {
      console.error('Erreur lors de la configuration de l\'email:', error);
      throw error;
    }
  }
}

export default new ContactService();
